import chalk from 'chalk';
import ora from 'ora';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';
import { resolveRules, groupRulesByTool, calculateScore } from '../../engine/rule-engine.js';
import { gitleaksRunner, semgrepRunner, osvScannerRunner, runBuiltinChecks } from '../../tools/index.js';
import type { CheckResult, CheckReport } from '../../types/index.js';

interface CheckOptions {
  fix?: boolean;
  changed?: boolean;
  json?: boolean;
}

export async function checkCommand(options: CheckOptions): Promise<void> {
  const cwd = process.cwd();
  const startTime = Date.now();

  const spinner = options.json ? null : ora('Loading configuration...').start();

  try {
    const loaded = await loadConfig(cwd);
    const config = loaded?.config ?? getDefaultConfig();

    if (spinner) spinner.text = 'Resolving rules...';
    const resolvedRules = resolveRules(config);
    const rulesByTool = groupRulesByTool(resolvedRules);

    const allResults: CheckResult[] = [];
    const toolStats: CheckReport['tools'] = [];

    if (spinner) spinner.text = 'Running builtin checks...';
    const builtinRules = rulesByTool.get('builtin') ?? [];
    if (builtinRules.length > 0) {
      const builtinStart = Date.now();
      const builtinResults = await runBuiltinChecks(
        cwd,
        builtinRules.map((r) => r.definition.id)
      );
      allResults.push(...builtinResults);
      toolStats.push({
        name: 'builtin',
        duration: Date.now() - builtinStart,
        results: builtinResults.length,
      });
    }

    if (config.tools?.gitleaks !== false) {
      if (spinner) spinner.text = 'Running gitleaks (secret scanning)...';
      const isInstalled = await gitleaksRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await gitleaksRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'gitleaks',
          version: (await gitleaksRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      } else if (spinner) {
        spinner.warn(chalk.yellow('gitleaks not installed, skipping secret scanning'));
        spinner.start();
      }
    }

    if (config.tools?.osvScanner !== false) {
      if (spinner) spinner.text = 'Running osv-scanner (dependency vulnerabilities)...';
      const isInstalled = await osvScannerRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await osvScannerRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'osv-scanner',
          version: (await osvScannerRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      } else if (spinner) {
        spinner.warn(chalk.yellow('osv-scanner not installed, skipping dependency scanning'));
        spinner.start();
      }
    }

    if (config.tools?.semgrep !== false) {
      if (spinner) spinner.text = 'Running semgrep (SAST)...';
      const isInstalled = await semgrepRunner.isInstalled();
      if (isInstalled) {
        const toolStart = Date.now();
        const results = await semgrepRunner.run(cwd);
        allResults.push(...results);
        toolStats.push({
          name: 'semgrep',
          version: (await semgrepRunner.getVersion()) ?? undefined,
          duration: Date.now() - toolStart,
          results: results.length,
        });
      } else if (spinner) {
        spinner.warn(chalk.yellow('semgrep not installed, skipping SAST'));
        spinner.start();
      }
    }

    const duration = Date.now() - startTime;
    const score = calculateScore(allResults);

    const report: CheckReport = {
      timestamp: new Date().toISOString(),
      duration,
      preset: config.preset,
      level: config.level ?? 'standard',
      summary: {
        total: allResults.length,
        errors: allResults.filter((r) => r.severity === 'error').length,
        warnings: allResults.filter((r) => r.severity === 'warn').length,
        infos: allResults.filter((r) => r.severity === 'info').length,
        fixed: 0,
      },
      score,
      results: allResults,
      tools: toolStats,
    };

    if (options.json) {
      console.log(JSON.stringify(report, null, 2));
      return;
    }

    if (spinner) spinner.stop();

    printReport(report);

    if (report.summary.errors > 0) {
      process.exit(1);
    }
  } catch (error) {
    if (spinner) spinner.fail(chalk.red('Check failed'));
    console.error(error);
    process.exit(1);
  }
}

function printReport(report: CheckReport): void {
  console.log('\n' + chalk.bold('Devrail Check Results'));
  console.log(chalk.dim('─'.repeat(50)));

  const scoreColor = report.score >= 80 ? chalk.green : report.score >= 50 ? chalk.yellow : chalk.red;
  console.log(`\nScore: ${scoreColor.bold(report.score + '/100')}`);

  console.log(`\nSummary:`);
  console.log(`  ${chalk.red('●')} Errors: ${report.summary.errors}`);
  console.log(`  ${chalk.yellow('●')} Warnings: ${report.summary.warnings}`);
  console.log(`  ${chalk.blue('●')} Info: ${report.summary.infos}`);

  if (report.results.length > 0) {
    console.log('\n' + chalk.bold('Issues:'));

    const grouped = new Map<string, CheckResult[]>();
    for (const result of report.results) {
      const key = result.file ?? 'General';
      const existing = grouped.get(key) ?? [];
      existing.push(result);
      grouped.set(key, existing);
    }

    for (const [file, results] of grouped) {
      console.log(`\n  ${chalk.cyan(file)}`);
      for (const result of results) {
        const severityIcon =
          result.severity === 'error'
            ? chalk.red('✖')
            : result.severity === 'warn'
              ? chalk.yellow('⚠')
              : chalk.blue('ℹ');
        const location = result.line ? `:${result.line}` : '';
        console.log(`    ${severityIcon} ${result.message}`);
        console.log(`      ${chalk.dim(result.ruleId)}${chalk.dim(location)}`);
      }
    }
  } else {
    console.log('\n' + chalk.green('✓ All clear! No issues found.'));
  }

  console.log('\n' + chalk.dim(`Duration: ${report.duration}ms`));
  console.log(chalk.dim(`Tools: ${report.tools.map((t) => t.name).join(', ')}`));
  console.log();
}
